import { action, computed, observable, runInAction } from 'mobx'
import { func, validationContext } from 'validx'
import { task } from 'mobx-task'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { ModalViewModel } from '@/infra/ModalViewModel'
import type { SubmittedJob } from '@/features/jobs/details/SubmittedJob'
import type { JobScopeChangeStore } from '@/features/jobs/job-scope-changes/JobScopeChangeStore'
import {
  JobScopeChange,
  JobScopeChangePriceBreakdown,
} from '@/features/jobs/job-scope-changes/JobScopeChange'
import type { SnackbarFactory } from '@/features/shell'
import type { NewBillingMethodDialogViewModel } from '@/features/billing/profile/NewBillingMethodDialog/NewBillingMethodDialogViewModel'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { CheckHasBillingMethodFn } from '@/features/billing'
import { tapAction } from '@/utils/mobx-util'
import type { ActivationLifecycle } from '@/infra/activation'

type DeclineReason = 'Expensive' | 'Alternative' | 'Confusing' | 'Other'

/**
 * The view model for the job scope change dialog.
 */
export class JobScopeChangeDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * The modal to display the price breakdown dialog.
   */
  readonly jobScopeChangeModal = new ModalViewModel()

  /**
   * The modal to display the decline dialog.
   */
  readonly declineModal = new ModalViewModel()

  /**
   * The modal to display the approved dialog.
   */
  readonly approvedModal = new ModalViewModel()

  /**
   * The job scope change.
   */
  @observable accessor jobScopeChange = JobScopeChange.none

  /**
   * The job scope change price breakdown.
   */
  @observable accessor jobScopeChangePriceBreakdown =
    JobScopeChangePriceBreakdown.none

  /**
   * The reason for declining the job scope change.
   */
  @observable accessor declineReason: DeclineReason = 'Expensive'

  /**
   * The feedback for declining the job scope change.
   */
  @observable accessor feedback = ''

  /**
   * Whether the current user has a billing method.
   */
  @observable accessor hasBillingMethod = false

  /**
   * The decline reasons with their descriptions.
   */
  readonly declineReasons: Record<DeclineReason, string> = {
    Expensive: 'The price is too high',
    Alternative: 'I found an alternative',
    Confusing: 'The scope is confusing',
    Other: 'Other',
  }

  /**
   * The maximum length of the feedback.
   */
  static readonly maxTextLength = 280

  /**
   * Validation.
   */
  validation = validationContext(this, {
    feedback: [
      func(
        () => this.declineReason !== 'Other' || this.feedback.length > 0,
        'You must provide a reason for declining.',
      ),
      func(
        () =>
          this.feedback.length <= JobScopeChangeDialogViewModel.maxTextLength,
        `Your answer must be less than ${JobScopeChangeDialogViewModel.maxTextLength} characters in length.`,
      ),
    ],
  })

  constructor(
    private readonly _job: SubmittedJob,
    private readonly _jobScopeChangeStore: JobScopeChangeStore,
    private readonly _newSnackbar: SnackbarFactory,
    private readonly _checkHasBillingMethod: CheckHasBillingMethodFn,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    public readonly newBillingMethodDialogVm: NewBillingMethodDialogViewModel,
  ) {
    const billingMethodCreated$ = this.newBillingMethodDialogVm.created$.pipe(
      tapAction(() => (this.hasBillingMethod = true)),
    )
    this._lifecycle.manage(billingMethodCreated$)
  }

  /**
   * Whether a billing method is needed.
   */
  @computed get isNewBillingMethodNeeded() {
    return (
      !this.hasBillingMethod &&
      this.jobScopeChangePriceBreakdown.amountDue !== 0
    )
  }

  /**
   * Whether pricing of scope changes is visible.
   */
  @computed get isPricingVisible() {
    return (
      this._job.isBillingEnabled &&
      this._getCurrentMember().permissions.viewJobPricing
    )
  }

  /**
   * The items to display in the job scope change dialog. Bundles are displayed first.
   */
  @computed get sortedItems() {
    return this.jobScopeChangePriceBreakdown.items.slice().sort((a, b) => {
      if (a.kind === 'Bundle' && b.kind !== 'Bundle') {
        return -1
      }
      if (a.kind !== 'Bundle' && b.kind === 'Bundle') {
        return 1
      }
      return 0
    })
  }

  /**
   * Shows the job scope change dialog.
   */
  show(jobScopeChange: JobScopeChange) {
    this.reset()
    this._lifecycle.signal.throwIfAborted()

    runInAction(() => (this.jobScopeChange = jobScopeChange))

    void this.fetchPriceBreakdown(jobScopeChange, this._lifecycle.signal)

    void this.checkHasDefaultBillingMethod()

    this.jobScopeChangeModal.show()
  }

  /**
   * Checks whether the member has a billing method.
   */
  checkHasDefaultBillingMethod = task.resolved(async () => {
    return this._checkHasBillingMethod(this._getCurrentMember()).then((x) =>
      runInAction(() => (this.hasBillingMethod = x)),
    )
  })

  /**
   * Resets the view model.
   */
  @action.bound
  reset() {
    this.jobScopeChange = JobScopeChange.none
    this.jobScopeChangePriceBreakdown = JobScopeChangePriceBreakdown.none

    this.resetDecline()
    this.fetchPriceBreakdown.reset()
  }

  /**
   * Resets the decline dialog.
   */
  @action.bound
  resetDecline() {
    this.declineReason = 'Expensive'
    this.feedback = ''
  }

  /**
   * Closes the dialog.
   */
  @action.bound
  close() {
    this.jobScopeChangeModal.close()
    this.approvedModal.close()
  }

  /**
   * Displays the new billing method modal.
   */
  @action.bound
  newBillingMethod() {
    this.newBillingMethodDialogVm.newMethod()
  }

  /**
   * Shows the decline dialog.
   */
  @action.bound
  decline() {
    this.declineModal.show()
  }

  /**
   * Accepts the job scope change.
   */
  acceptChanges = task.resolved(async () => {
    const snackbar = this._newSnackbar((s) => s.autoDismiss())

    try {
      await this._jobScopeChangeStore.approveJobScopeChange(this._job.id)

      this.jobScopeChangeModal.close()
      this.approvedModal.show()
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  })

  /**
   * Declines the job scope change.
   */
  declineChanges = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const snackbar = this._newSnackbar((s) =>
      s
        .saying('Success!', 'You declined the changes.')
        .type('success')
        .autoDismiss(),
    )

    try {
      const reason =
        this.declineReason === 'Other'
          ? this.feedback
          : this.declineReasons[this.declineReason]

      await this._jobScopeChangeStore.declineJobScopeChange({
        jobId: this._job.id,
        reason,
      })

      snackbar.show()

      this.declineModal.close()
      this.validation.reset()
      this.resetDecline()
    } catch (err: unknown) {
      snackbar.fromError(err)
    }
  })

  /**
   * Goes back to the job scope change dialog.
   */
  @action.bound
  goBack() {
    this.jobScopeChangeModal.show()
    this.declineModal.close()

    this.resetDecline()
  }

  /** @inheritdoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritdoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Fetches the price breakdown.
   */
  fetchPriceBreakdown = task.resolved(
    async (jobScopeChange: JobScopeChange, signal: AbortSignal) => {
      const priceBreakdown =
        await this._jobScopeChangeStore.fetchJobScopeChangePriceBreakdown({
          id: jobScopeChange.id,
          signal,
        })

      runInAction(() => (this.jobScopeChangePriceBreakdown = priceBreakdown))
    },
  )
}
